import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
    IonModal,
    IonIcon,
    IonCard,
    IonCardContent,
    IonList,
    IonItem,
    IonLabel,
    IonGrid,
    IonRow,
    IonCol,
    IonNote,
    IonSpinner,
} from "@ionic/react";
import { closeOutline, eyeOutline } from "ionicons/icons";
import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Typography,
    Box,
    Alert,
    TablePagination,
} from "@mui/material";
import axios from "axios";
import {getAppSettings, baseUrl, requestID, config} from "../../../../common/utils";

/* ================= TYPES ================= */

interface ApiItem {
    name: string;
    price: string;
    description: string;
    quantity: number;
}

interface ApiInvoice {
    id: number;
    invoice_name: string;
    invoice_id: string;
    purchase_order_id: string;
    vendor_id: string;
    total_price: string;
    status: string;
    created_at: string;
    updated_at: string;
    vendor_full_name: string;
    items: ApiItem[];
    number_of_items: number;
    name: string;
}

interface ApiResponse {
    total: number;
    data: ApiInvoice[];
}

interface ProductItem {
    id: number;
    productName: string;
    quantity: number;
    unitPrice: number;
}

interface Invoice {
    dbId: number;
    displayId: string;
    invoiceName: string;
    price: number;
    status: string;
    dateRequested: string;
    requestedBy: string;
    approvedBy: string | null;
    items: ProductItem[];
}

/* ================= COMPONENT ================= */

const InvoiceDashboard = () => {
    const { siteName } = getAppSettings();

    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // --- Pagination States ---
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    /* ================= FETCH INVOICES (AXIOS POST) ================= */

    useEffect(() => {
        fetchInvoices();
    }, [page, rowsPerPage]);

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            setError(null);

            const offset = page * rowsPerPage;

            // 1. Create FormData object
            const formData = new FormData();
            formData.append("requestID", requestID());
            formData.append('limit', String(rowsPerPage));
            formData.append('offset', String(offset));

            // 2. Change GET to POST and pass formData as the second argument
            // Note: Axios automatically sets the Content-Type to multipart/form-data
            const response = await axios.post<ApiResponse>(
                `${baseUrl()}Geacloud_Invoices/get_invoices`,
                formData, config
            );

            console.log(response)

            const json = response.data;

            setTotalCount(json.total || 0);

            const apiData = json.data || [];

            const mappedInvoices: Invoice[] = apiData.map((inv) => ({
                dbId: inv.id,
                displayId: inv.invoice_id,
                invoiceName: inv.invoice_name,
                price: Number(inv.total_price),
                status: inv.status,
                dateRequested: inv.created_at,
                requestedBy: inv.vendor_full_name || inv.name || inv.vendor_id,
                approvedBy: null,
                items: inv.items.map((item, index) => ({
                    id: index + 1,
                    productName: item.name,
                    quantity: item.quantity,
                    unitPrice: Number(item.price),
                })),
            }));

            setInvoices(mappedInvoices);
        } catch (err: any) {
            console.error(err);
            const errorMessage = err.response?.data?.message || err.message || "Failed to fetch invoices";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    /* ================= HELPERS ================= */

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Paid":
            case "Approved":
                return "success";
            case "Cancelled":
            case "Rejected":
                return "error";
            case "Unpaid":
                return "warning";
            case "Pending":
            default:
                return "default";
        }
    };

    const handleOpenModal = (invoice: Invoice) => {
        setSelectedInvoice(invoice);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedInvoice(null);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    /* ================= RENDER ================= */

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonMenuButton />
                    </IonButtons>
                    <IonTitle> Invoices </IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="ion-padding">
                <Box sx={{ p: 2 }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                        Recent Invoices
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Paper
                        sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            backgroundColor: "rgba(15,23,42,0.85)",
                            backdropFilter: "blur(6px)",
                            boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {loading && invoices.length === 0 ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                height="300px"
                            >
                                <IonSpinner name="crescent" />
                            </Box>
                        ) : (
                            <>
                                <TableContainer>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                {["Invoice Name", "Price", "Status", "Items", "Action"].map(
                                                    (header) => (
                                                        <TableCell
                                                            key={header}
                                                            sx={{
                                                                backgroundColor: "rgba(2,6,23,0.95)",
                                                                color: "#cbd5f5",
                                                                fontWeight: 600,
                                                                fontSize: "0.75rem",
                                                                letterSpacing: "0.08em",
                                                                textTransform: "uppercase",
                                                            }}
                                                            align={
                                                                header === "Items" || header === "Action"
                                                                    ? "center"
                                                                    : "left"
                                                            }
                                                        >
                                                            {header}
                                                        </TableCell>
                                                    )
                                                )}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {invoices.length === 0 && !loading ? (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={5}
                                                        align="center"
                                                        sx={{
                                                            py: 6,
                                                            color: "#94a3b8",
                                                            fontStyle: "italic",
                                                        }}
                                                    >
                                                        No invoice found
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                invoices.map((invoice) => (
                                                    <TableRow
                                                        key={invoice.dbId}
                                                        hover
                                                        sx={{
                                                            transition: "background-color 0.2s ease",
                                                            "&:hover": {
                                                                backgroundColor: "rgba(148,163,184,0.08)",
                                                            },
                                                        }}
                                                    >
                                                        <TableCell sx={{ color: "#e5e7eb" }}>
                                                            <Typography fontWeight={500}>
                                                                {invoice.invoiceName}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ color: "#94a3b8" }}
                                                            >
                                                                {invoice.displayId}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell sx={{ color: "#e5e7eb" }}>
                                                            ₦{invoice.price.toLocaleString()}
                                                        </TableCell>

                                                        <TableCell>
                                                            <Chip
                                                                label={invoice.status}
                                                                color={getStatusColor(invoice.status) as any}
                                                                size="small"
                                                                sx={{ fontWeight: 500, borderRadius: "6px" }}
                                                            />
                                                        </TableCell>

                                                        <TableCell align="center" sx={{ color: "#e5e7eb" }}>
                                                            {invoice.items.length}
                                                        </TableCell>

                                                        <TableCell align="center">
                                                            <IonButton
                                                                fill="clear"
                                                                size="small"
                                                                style={{ opacity: 0.85, textTransform: 'none' }}
                                                                onClick={() => handleOpenModal(invoice)}
                                                            >
                                                                <span style={{ marginRight: "6px", fontWeight: 500 }}>See More</span>
                                                                <IonIcon icon={eyeOutline} />
                                                            </IonButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    count={totalCount}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    sx={{
                                        color: "#cbd5f5",
                                        borderTop: "1px solid rgba(148,163,184,0.2)",
                                        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                                            marginTop: "0",
                                            marginBottom: "0"
                                        },
                                        ".MuiTablePagination-selectIcon": {
                                            color: "#cbd5f5"
                                        }
                                    }}
                                />
                            </>
                        )}
                    </Paper>
                </Box>

                {/* ================= MODAL ================= */}

                <IonModal
                    isOpen={showModal}
                    onDidDismiss={handleCloseModal}
                    style={{
                        "--width": "92%",
                        "--max-width": "520px",
                        "--max-height": "90vh",
                        "--border-radius": "16px",
                        "--box-shadow": "0 20px 50px rgba(0,0,0,0.6)",
                    }}
                >
                    <IonHeader>
                        <IonToolbar>
                            <IonTitle>Invoice Details</IonTitle>
                            <IonButtons slot="end">
                                <IonButton onClick={handleCloseModal}>
                                    <IonIcon icon={closeOutline} />
                                </IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>

                    <IonContent className="ion-padding">
                        {selectedInvoice && (
                            <>
                                <IonCard
                                    style={{
                                        borderRadius: "16px",
                                        boxShadow: "0 10px 25px rgba(0,0,0,0.4)",
                                    }}
                                >
                                    <IonCardContent>
                                        <IonGrid>
                                            <IonRow>
                                                <IonCol size="12">
                                                    <Typography variant="h6">
                                                        {selectedInvoice.invoiceName}
                                                    </Typography>
                                                    <IonNote>{selectedInvoice.displayId}</IonNote>
                                                </IonCol>
                                            </IonRow>

                                            <IonRow className="ion-margin-top">
                                                <IonCol size="6">
                                                    <IonLabel color="medium">Requested By</IonLabel>
                                                    <Typography>{selectedInvoice.requestedBy}</Typography>
                                                </IonCol>
                                                <IonCol size="6">
                                                    <IonLabel color="medium">Date</IonLabel>
                                                    <Typography>{selectedInvoice.dateRequested}</Typography>
                                                </IonCol>
                                            </IonRow>

                                            <IonRow className="ion-margin-top">
                                                <IonCol size="6">
                                                    <IonLabel color="medium">Status</IonLabel>
                                                    <Chip
                                                        label={selectedInvoice.status}
                                                        color={getStatusColor(selectedInvoice.status) as any}
                                                        size="small"
                                                    />
                                                </IonCol>
                                                <IonCol size="6">
                                                    <IonLabel color="medium">Approved By</IonLabel>
                                                    <Typography>
                                                        {selectedInvoice.approvedBy || "N/A"}
                                                    </Typography>
                                                </IonCol>
                                            </IonRow>
                                        </IonGrid>
                                    </IonCardContent>
                                </IonCard>

                                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                                    Items Breakdown
                                </Typography>

                                <IonList>
                                    {selectedInvoice.items.map((item) => (
                                        <IonItem key={item.id} lines="none">
                                            <IonLabel>
                                                <h2>{item.productName}</h2>
                                                <p>
                                                    Qty: {item.quantity} × ₦
                                                    {item.unitPrice.toLocaleString()}
                                                </p>
                                            </IonLabel>
                                            <IonNote slot="end" style={{ fontWeight: 600 }}>
                                                ₦{(item.quantity * item.unitPrice).toLocaleString()}
                                            </IonNote>
                                        </IonItem>
                                    ))}

                                    <IonItem color="light">
                                        <IonLabel>
                                            <strong>Total</strong>
                                        </IonLabel>
                                        <IonNote
                                            slot="end"
                                            color="primary"
                                            style={{ fontSize: "1.1em", fontWeight: "bold" }}
                                        >
                                            ₦{selectedInvoice.price.toLocaleString()}
                                        </IonNote>
                                    </IonItem>
                                </IonList>
                            </>
                        )}
                    </IonContent>
                </IonModal>
            </IonContent>
        </IonPage>
    );
};

export default InvoiceDashboard;